"use client"

import { t } from "@/languages/i18n"
import { DefaultImage } from "@/lib/external-components"
import { getLocalizedHref } from "@/lib/locale-utils"
import { cleanDescription, formatTimeAgo, generateImagePath } from "@/lib/utils"
import { BreakingPostsProps } from "@/types/types"
import Image from "next/image"
import Link from "next/link"

const BreakingPosts = ({ posts, locale = "vi" }: BreakingPostsProps) => {
  if (posts.length === 0) return null

  return (
    <div className="flex flex-col w-full sm:gap-4 gap-5">
      <h2 className="text-[0.8125rem] leading-[20.15px] sm:font-extrabold text-public-fg-primary uppercase">
        {t("Tin nổi bật")}
      </h2>
      <div className="flex flex-col gap-0 max-h-[700px] overflow-y-auto">
        {posts.map((post, index) => {
          const blogUrl = getLocalizedHref(post.attributes.locale, `/${post.attributes.slug}`)
          const imgAttr =
            post.attributes?.cover?.data?.attributes || post.attributes?.image?.data?.attributes
          const imageUrl = imgAttr?.url
            ? generateImagePath(imgAttr.url, "thumbnail", imgAttr.formats)
            : DefaultImage
          const postTitle = post.attributes?.name || post.attributes?.title || ""
          const description = post.attributes?.excerpt || post.attributes?.description || ""
          const publishedAt = post.attributes?.publishedAt || post.attributes?.updatedAt || ""
          const timeAgo = publishedAt ? formatTimeAgo(publishedAt, locale) : ""

          const cleanedDescription = cleanDescription(description)

          return (
            <div key={post.id} className="flex flex-col">
              <Link href={blogUrl} className="group flex flex-col gap-2">
                <h3 className="sm:text-lg sm:font-bold text-public-fg sm:leading-[23.4px] text-base leading-[20.8px] font-semibold group-hover:text-public-brand-strong transition-colors line-clamp-3">
                  {postTitle}
                </h3>

                <div className="flex flex-row gap-3">
                  <div className="relative overflow-hidden w-[150px] h-[100px] flex-shrink-0">
                    <Image
                      src={imageUrl}
                      alt={postTitle}
                      fill
                      sizes="150px"
                      className="object-cover transition-opacity duration-300 group-hover:opacity-90"
                      loading="lazy"
                      quality={70}
                    />
                  </div>

                  <div className="flex flex-col justify-around">
                    {cleanedDescription && (
                      <p className="text-sm text-public-fg leading-relaxed line-clamp-3">{cleanedDescription}</p>
                    )}
                    {timeAgo && <p className="text-xs text-public-fg font-normal">{timeAgo}</p>}
                  </div>
                </div>
              </Link>
              {index < posts.length - 1 && (
                <div className="border-b border-dashed border-public-border-dashed sm:my-[26px] my-4"></div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default BreakingPosts
